import { useEffect, useState } from "react";
import { vocabularize } from "src/Common/vocab";

// Type-to-confirm dialog for the irreversible tenant wipe. Deleting a tenant
// destroys every one of its work orders, users, customers, tickets and files,
// so the Delete button stays disabled until the exact identifier is typed —
// a single misclick cannot trigger it.
function DeleteTenantDialog({ company, onConfirm, onCancel, isDeleting }) {
  const [typed, setTyped] = useState("");

  // The dialog stays mounted between opens (it just renders null when closed),
  // so clear the field each time it opens for a tenant — otherwise the second
  // open still holds the previous identifier and its Delete button is live on
  // arrival.
  useEffect(() => {
    setTyped("");
  }, [company]);

  if (!company) return null;

  const identifier = company.identifier || "";
  const matches = typed.trim() === identifier;

  return (
    <div className="fixed inset-0 w-screen h-screen bg-black bg-opacity-40 flex items-center justify-center z-[60]">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[440px] bg-white rounded-[12px] p-6 flex flex-col border border-borderColor"
      >
        <p className="text-[18px] font-OpenSansSemiBold text-titleColor">Delete “{company.name}”?</p>
        <p className="text-[13px] font-OpenSansRegular text-subTitleColor leading-[20px] mt-2">
          This permanently deletes the tenant and <b>all of its data</b> — every {vocabularize("work order")}, user, customer,
          ticket, and uploaded file. This cannot be undone.
        </p>

        <p className="text-[13px] font-OpenSansRegular text-titleColor mt-4">
          Type <span className="font-OpenSansSemiBold">{identifier}</span> to confirm:
        </p>
        <input
          value={typed}
          autoFocus
          onChange={(e) => setTyped(e.target.value)}
          placeholder={identifier}
          className={`w-full h-[38px] border-solid border-[1px] rounded-[8px] px-3 mt-2 text-[14px] font-OpenSansRegular text-titleColor outline-none ${
            typed && !matches ? "border-red-500" : "border-borderColor"
          }`}
        />

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="text-[13px] font-OpenSansSemiBold text-subTitleColor border border-borderColor rounded-[8px] px-5 h-[36px] cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={() => matches && onConfirm(company)}
            disabled={!matches || isDeleting}
            className={`text-[13px] font-OpenSansSemiBold text-white rounded-[8px] px-5 h-[36px] ${
              matches && !isDeleting ? "bg-red-600 cursor-pointer" : "bg-red-300 cursor-not-allowed"
            }`}
          >
            {isDeleting ? "Deleting…" : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteTenantDialog;
